import { Box, Modal } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { CallV3Context } from "../state/CallV3Context";
import { ClientContext } from "../state/ClientContext";
import { getFirstName } from "../utils/fieldAccessors";
import declineCallBtn from "../assets/declineCallBtn.png";
import joinCallBtn from "../assets/joinCallBtn.png";

const MissedCallModal = () => {
  const { selectedClient } = useContext(ClientContext);
  const {
    callEndState,
    callerName,
    placeCall,
    clearEndCallState,
    callId,
    submitCallDescription,
    callType,
  } = useContext(CallV3Context);

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [showNotesInput, setShowNotesInput] = useState(false);
  const [notes, setNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const allowNotes = callEndState !== "busy" && callEndState !== "offline";

  const isMissedState =
    callEndState === "missed" ||
    callEndState === "cancelled" ||
    callEndState === "busy" ||
    callEndState === "rejected" ||
    callEndState === "offline";

  useEffect(() => {
    if (isMissedState) {
      // Don't show modal for emergency calls - auto-clear state instead
      if (callType === "emergency") {
        clearEndCallState();
        return;
      }
      setIsOpenModal(true);
    }
  }, [isMissedState, callType, clearEndCallState]);

  useEffect(() => {
    if (!allowNotes) {
      setShowNotesInput(false);
      setNotes("");
    }
  }, [allowNotes]);

  const handleClose = () => {
    setIsOpenModal(false);
    setShowNotesInput(false);
    setNotes("");
    setIsSaving(false);
    clearEndCallState();
  };

  const handleCallAgain = () => {
    handleClose();
    placeCall();
  };

  const handleAddNotes = () => {
    setShowNotesInput(true);
  };

  const handleSaveNotes = async () => {
    if (!callId) {
      handleClose();
      return;
    }
    setIsSaving(true);
    try {
      await submitCallDescription(notes || undefined);
    } catch (error) {
      console.error("Failed to save notes:", error);
    }
    setIsSaving(false);
    handleClose();
  };

  const handleSkipNotes = async () => {
    if (callId) {
      try {
        await submitCallDescription(undefined);
      } catch (error) {
        console.warn("Skip notes failed:", error);
      }
    }
    handleClose();
  };

  const clientName = getFirstName(selectedClient, "Client");
  const displayName = callerName || clientName;

  const getMessage = () => {
    switch (callEndState) {
      case "missed":
        return callerName
          ? {
              title: `You missed a call from ${callerName}`,
              subtitle: `We added this missed call to your call history.`,
            }
          : {
              title: `Looks like ${displayName} isn't available right now`,
              subtitle: `We added this missed call to the client's call history.`,
            };
      case "busy":
        return callerName
          ? {
              title: `Looks like ${displayName} is on the other line`,
              subtitle: `${displayName} is currently on another call. We added this missed call to your call history.`,
            }
          : {
              title: `${displayName} is currently on another call`,
              subtitle: `${displayName} is busy right now. Try again later.`,
            };
      case "offline":
        return {
          title: `${displayName} is offline`,
          subtitle: `${displayName} is not available right now. Try again later.`,
        };
      case "cancelled":
        return callerName
          ? {
              title: `${callerName} cancelled the call`,
              subtitle: `${callerName} ended the call before you could answer.`,
            }
          : {
              title: `Call to ${displayName} was cancelled`,
              subtitle: `The call was cancelled before ${displayName} could answer.`,
            };
      case "rejected":
        return callerName || !callId
          ? null
          : {
              title: `${displayName} rejected your call`,
              subtitle: `${displayName} declined the call. We added this to your call history.`,
            };
      default:
        return { title: "Call Status", subtitle: "Check your call history." };
    }
  };

  const message = getMessage();
  const { title, subtitle } = message || {};

  // Don't show missed call modal for emergency calls - they use round-robin
  // and the modal blocks receiving subsequent incoming calls
  if (!isMissedState || !message || callType === "emergency") return null;

  return (
    <Modal disableEscapeKeyDown open={isOpenModal}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 2,
          backgroundColor: "#d0d0d0",
          borderColor: "grey.900",
          borderWidth: "3px",
          p: 3,
          color: "#000000",
        }}
      >
        <h3 style={{ textAlign: "center" }}>
          <strong>{title}</strong>
        </h3>
        <br />
        <h5 style={{ textAlign: "center" }}>{subtitle}</h5>

        {allowNotes && !showNotesInput && (
          <div
            style={{
              textAlign: "center",
              marginTop: "10px",
              marginBottom: "10px",
            }}
          >
            <button
              onClick={handleAddNotes}
              className="btn-primary"
              style={{ minWidth: "160px" }}
            >
              Add notes
            </button>
          </div>
        )}
        {allowNotes && showNotesInput && (
          <div style={{ marginTop: "20px", marginBottom: "10px" }}>
            <textarea
              style={{ width: "100%", minHeight: "120px" }}
              placeholder="Add notes about this missed call"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={isSaving}
            />
          </div>
        )}

        <div
          className="callButtonsContainer"
          style={{ gap: "20px", alignItems: "center" }}
        >
          {!showNotesInput && (
            <div
              className="declineCallContainer"
              style={{ marginRight: "30px" }}
            >
              <img
                onClick={handleClose}
                src={declineCallBtn}
                alt="Close"
                style={{ width: "100px" }}
              />
              <button onClick={handleClose} disabled={isSaving}>
                Close
              </button>
            </div>
          )}

          {allowNotes && showNotesInput && (
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={handleSaveNotes}
                disabled={isSaving}
                className="btn-primary"
              >
                {isSaving ? "Saving..." : "Save notes"}
              </button>
              <button
                onClick={handleSkipNotes}
                disabled={isSaving}
                className="btn-primary"
              >
                Skip
              </button>
            </div>
          )}

          {!showNotesInput && (
            <div className="joinCallContainer">
              <img
                onClick={handleCallAgain}
                src={joinCallBtn}
                alt="Call Again"
                style={{ width: "100px", cursor: "pointer" }}
              />
              <button onClick={handleCallAgain} disabled={isSaving}>
                {callerName ? "Call back" : "Call again"}
              </button>
            </div>
          )}
        </div>
      </Box>
    </Modal>
  );
};

export default MissedCallModal;
